/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from 'react';
import { DataTable, DataTableFilterMeta, DataTableSelectionSingleChangeEvent } from 'primereact/datatable';
import { Column, ColumnEditorOptions } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Panel } from 'primereact/panel';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { LetteringService } from '../../service/LetteringService';
import { Button } from 'primereact/button';
import { Checkbox } from "primereact/checkbox";
import { Controller, useForm } from 'react-hook-form';
import { classNames } from 'primereact/utils';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import LetteringSequence from '../../models/LetteringSequence';
import { msalInstance } from '../../service/msal';
import { Link, useParams } from 'react-router-dom';
import { FilterMatchMode } from 'primereact/api';

const StyleLettering = () => {
    const emptyLetteringSequence : LetteringSequence = {
        id: 0,
        transfer_Style: '',
        sequence: 2,
        raw_Material_Number: '',
        quantity_Per_Letter: false,
        estimated_Count_Color1: 0,
        estimated_Count_Color2: 0,
        estimated_Count_Color3: 0,
        letter_Size: 0,
        word_Number: 0,
        entry_User: '',
        entry_Datetime: '',
        change_User: '',
        change_Datetime: ''
    };

    const [letteringSequences, setLetteringSequences] = useState<LetteringSequence[]>([]);
    const [loadingLetteringSequences, setLoadingLetteringSequences] = useState(true);
    const [selectedLetteringSequence] = useState<LetteringSequence>();
    const [letteringSequence, setLetteringSequence] = useState<LetteringSequence>(emptyLetteringSequence);
    const [deleteLetteringSequenceDialog, setDeleteLetteringSequenceDialog] = useState(false);
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [filters, setFilters] = useState<DataTableFilterMeta>({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const[editingRows, setEditingRows] = useState({});
    const isNew = useRef(false);
    const toast = useRef<any>(null);
    const account = msalInstance.getActiveAccount();
    const { styleCode } = useParams();
    
    useEffect(() => {
        if (!styleCode) { return; }

        LetteringService.getLetteringSequencesByStyle(styleCode).then(data => {
            setLetteringSequences(data)
            setLoadingLetteringSequences(false);
        }); 
    }, []);

    const {
        control: controlLetteringSequence,
        clearErrors: letteringSequenceClearErrors,
        setError: letteringSequenceSetError,
        formState: { errors: errorsLetteringSequence },
        reset: resetLetteringSequence,
        handleSubmit: handleSubmitLetteringSequence } = useForm();

    const formEditLetteringSequence = useForm({ mode: "all"});

    const onSubmitLetteringSequence = (data : any) => {
        if (!styleCode) { return; }

        isNew.current = true;
        if (letteringSequences){
            let checkDuplicate = false;
            letteringSequences.forEach(letteringSequence => {
                if (letteringSequence.transfer_Style === styleCode
                    && letteringSequence.sequence === data.sequence 
                    && letteringSequence.letter_Size === data.letter_Size 
                    && letteringSequence.word_Number === data.word_Number) {
                        checkDuplicate = true;
                        return;
                }
            });
    
            if (checkDuplicate) {
                letteringSequenceSetError("sequence", { type: "error" });
                letteringSequenceSetError("letter_Size", { type: "error" });
                letteringSequenceSetError("word_Number", { type: "error" });
                toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Duplicate entry detected based on sequence, style, letter size and word number.', life: 3000});
                return;
            }
        }
        
        if (data.letter_Size != null && data.letter_Size > 99.99) {
            data.letter_Size = 99.99;
        }

        let _colorQtySum = data.estimated_Count_Color1 + data.estimated_Count_Color2 + data.estimated_Count_Color3;

        if (_colorQtySum === 0) {
            letteringSequenceSetError("estimated_Count_Color1", { type: "error" });
            letteringSequenceSetError("estimated_Count_Color2", { type: "error" });
            letteringSequenceSetError("estimated_Count_Color3", { type: "error" });
            toast.current.show({ severity: 'error', summary: 'Failed', detail: 'At least one quantity color should be filled.', life: 3000});
            return;
        } else {
            letteringSequenceClearErrors();
        }

        let _letteringSequence = { ...letteringSequence };
        _letteringSequence.transfer_Style = styleCode;
        _letteringSequence.sequence = data.sequence;
        _letteringSequence.raw_Material_Number = data.raw_Material_Number;
        _letteringSequence.quantity_Per_Letter = data.quantity_Per_Letter;
        _letteringSequence.estimated_Count_Color1 = data.estimated_Count_Color1;
        _letteringSequence.estimated_Count_Color2 = data.estimated_Count_Color2;
        _letteringSequence.estimated_Count_Color3 = data.estimated_Count_Color3;
        _letteringSequence.letter_Size = data.letter_Size;
        _letteringSequence.word_Number = data.word_Number;
        _letteringSequence.entry_User = account !== null ? account.username : '';
        _letteringSequence.entry_Datetime = new Date().toISOString().slice(0, 19).replace('T', ' ');  

        saveLetteringSequence(_letteringSequence);
    };

    const getLetteringSequenceFormErrorMessage = (name : string) => {
        return errorsLetteringSequence[name] ? <small className="p-error">{(errorsLetteringSequence[name] as any).message}</small> : <small className="p-error">&nbsp;</small>;
    };

    const hideDeleteLetteringSequenceDialog = () => {
        setDeleteLetteringSequenceDialog(false);
    }

    const saveLetteringSequence = (letteringSequenceData : LetteringSequence) => {
        if (!styleCode) { return; }

        let _letteringSequences = [...letteringSequences];

        if (isNew.current) {
            LetteringService.addLetteringSequence(letteringSequenceData).then((data) => {
                if (data){
                    toast.current.show({ severity : 'success', summary: 'Successful', detail: 'Lettering Sequence Created', life: 3000});
                    
                    LetteringService.getLetteringSequencesByStyle(styleCode).then(letteringSequenceData => {
                        setLetteringSequences(letteringSequenceData)
                        setLoadingLetteringSequences(false);
                    }); 

                    setLetteringSequence(emptyLetteringSequence);
                    resetLetteringSequence();
                } else {
                    toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Failed to Create Lettering Sequence', life: 3000});
                }
            });
        } else {
            LetteringService.updateLetteringSequence(letteringSequenceData).then((data) => {
                if (data) {
                    let index = letteringSequences.findIndex(r => r.id === letteringSequenceData.id);
                    _letteringSequences[index] = letteringSequenceData;
                    setLetteringSequences(_letteringSequences);
                    toast.current.show({ severity: 'success', summary: "Successful", detail: 'Lettering Sequence Updated', life: 3000});
                    setLetteringSequence(emptyLetteringSequence);
                } else {
                    toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Failed to Update Lettering Sequence', life: 3000});
                }
            });
        }
    } 

    const confirmDeleteLetteringSequence = (letteringSequence : LetteringSequence) => {
        setLetteringSequence(letteringSequence);
        setDeleteLetteringSequenceDialog(true);
    }

    const deleteLetteringSequence = () => {
        LetteringService.deleteLetteringSequence(letteringSequence.id, letteringSequence.transfer_Style).then((data) => {
            if (data) {
                let _letteringSequences = letteringSequences.filter(val => val.id !== letteringSequence.id);
                setLetteringSequences(_letteringSequences);
                setDeleteLetteringSequenceDialog(false);
                setLetteringSequence(emptyLetteringSequence);
                toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Lettering Sequence Deleted', life: 3000 });
            } else {
                toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Failed to Delete Lettering Sequence'})
            }
        });
    }

    const onRowEditChange = (e: any) => {
        setEditingRows(e.data);
    }
    
    const setLetteringSequenceActiveRowIndex = (rowData: DataTableSelectionSingleChangeEvent<LetteringSequence[]>) => {
        let _letteringSequence = (rowData.value as LetteringSequence);
        let selectedLetteringSequenceIndex = letteringSequences.findIndex(v => v.id === _letteringSequence.id);
        let _editingRows = { ...editingRows, ...{ [`${letteringSequences[selectedLetteringSequenceIndex].id}`]: true } };
        if (Object.keys(_editingRows).length === 1) {
            setEditingRows(_editingRows);
        }
    }

    const cellEditor = (options: ColumnEditorOptions) => {
        switch(options.field) {
            case 'raw_Material_Number':
                return raw_Material_Number_Editor(options);
            case 'quantity_Per_Letter':
                return quantity_Per_Letter_Editor(options);
            case 'letter_Size':
                return letter_Size_Editor(options);
            case 'word_Number':
                return word_Number_Editor(options);
            case 'sequence':
                return sequence_Editor(options);
            case 'estimated_Count_Color1':
                return estimated_Count_Color1_Editor(options);
            case 'estimated_Count_Color2':
                return estimated_Count_Color2_Editor(options);
            case 'estimated_Count_Color3':
                return estimated_Count_Color3_Editor(options);
        }
    }

    const quantity_Per_Letter_Editor = (options: any) => {
        return (
            <Controller 
                name="quantity_Per_Letter_Edit" 
                control={formEditLetteringSequence.control}
                defaultValue = {options.value}
                render={({ field, fieldState }) => {
                    letteringSequence.quantity_Per_Letter = field.value;

                    return (
                        <>
                            <Checkbox inputId={field.name} checked={fieldState.isDirty ? field.value : options.value} inputRef={field.ref} className={classNames({ 'p-invalid mr-1': fieldState.error })} onChange={(e) => { letteringSequence.quantity_Per_Letter = e.target.value; field.onChange(e.checked) }} />
                        </>
                    )}
                }
            />
        );
    }

    const sequence_Editor = (options: any) => {
        return (
            <Controller 
                name="sequence_Edit" 
                control={formEditLetteringSequence.control}
                defaultValue = {options.value}
                render={({ field, fieldState }) => {
                    letteringSequence.sequence = field.value;

                    return (
                        <>
                            <InputNumber id={field.name} name={field.name} inputRef={field.ref} value={fieldState.isDirty ? field.value : options.value} tooltip={fieldState.error?.message} tooltipOptions={{ position: 'bottom' }} onBlur={field.onBlur} onChange={(e) => field.onChange(e.value)} inputClassName={classNames('w-5rem', { 'p-invalid border-left-10': fieldState.error })} min={0}  />
                        </>
                    )} 
                }
            />              
        );
    }

    const estimated_Count_Color1_Editor = (options: any) => {
        return (
            <Controller 
                name="estimated_Count_Color1_Edit" 
                control={formEditLetteringSequence.control}
                defaultValue = {options.value}
                render={({ field, fieldState }) => {
                    letteringSequence.estimated_Count_Color1 = field.value;

                    return (
                        <>
                            <InputNumber id={field.name} name={field.name} inputRef={field.ref} value={fieldState.isDirty ? field.value : options.value} tooltip={fieldState.error?.message} tooltipOptions={{ position: 'bottom' }} onBlur={field.onBlur} onValueChange={(e) => field.onChange(e)} inputClassName={classNames('w-5rem', { 'p-invalid border-left-10': fieldState.error })} min={0} max={2147483647} useGrouping={false} placeholder="0" />
                        </>
                    )} 
                }
            />              
        );
    }

    const estimated_Count_Color2_Editor = (options: any) => {
        return (
            <Controller 
                name="estimated_Count_Color2_Edit" 
                control={formEditLetteringSequence.control}
                defaultValue = {options.value}
                render={({ field, fieldState }) => {
                    letteringSequence.estimated_Count_Color2 = field.value;

                    return (
                        <>
                            <InputNumber id={field.name} name={field.name} inputRef={field.ref} value={fieldState.isDirty ? field.value : options.value} tooltip={fieldState.error?.message} tooltipOptions={{ position: 'bottom' }} onBlur={field.onBlur} onValueChange={(e) => field.onChange(e)} inputClassName={classNames('w-5rem', { 'p-invalid border-left-10': fieldState.error })} min={0} max={2147483647} useGrouping={false} placeholder="0" />
                        </>
                    )} 
                }
            />              
        );
    }

    const estimated_Count_Color3_Editor = (options: any) => {
        return (
            <Controller 
                name="estimated_Count_Color3_Edit" 
                control={formEditLetteringSequence.control}
                defaultValue = {options.value}
                render={({ field, fieldState }) => {
                    letteringSequence.estimated_Count_Color3 = field.value;

                    return (
                        <>
                            <InputNumber id={field.name} name={field.name} inputRef={field.ref} value={fieldState.isDirty ? field.value : options.value} tooltip={fieldState.error?.message} tooltipOptions={{ position: 'bottom' }} onBlur={field.onBlur} onValueChange={(e) => field.onChange(e)} inputClassName={classNames('w-5rem', { 'p-invalid border-left-10': fieldState.error })} min={0} max={2147483647} useGrouping={false} placeholder="0" />
                        </>
                    )} 
                }
            />              
        );
    }

    const letter_Size_Editor = (options: any) => {
        return (
            <Controller 
                name="letter_Size_Edit" 
                control={formEditLetteringSequence.control}
                defaultValue = {options.value}
                render={({ field, fieldState }) => {
                    letteringSequence.letter_Size = field.value;

                    return (
                        <>
                            <InputNumber id={field.name} name={field.name} inputRef={field.ref} value={fieldState.isDirty ? field.value : options.value} tooltip={fieldState.error?.message} tooltipOptions={{ position: 'bottom' }} onBlur={field.onBlur} inputClassName={classNames('w-5rem', { 'p-invalid border-left-10': fieldState.error })} onChange={(e) => field.onChange(e.value)}
                                max={99.99}
                                maxFractionDigits={2}
                                format={true}
                                mode='decimal' minFractionDigits={0} 
                                placeholder="00.00" />
                        </>
                    )} 
                }
            />              
        );
    }

    const raw_Material_Number_Editor = (options: any) => {
        return (
            <Controller 
                name="raw_Material_Number_Edit" 
                control={formEditLetteringSequence.control}
                defaultValue = {options.value}
                rules={{ 
                    required: 'Raw Material Number is required',
                }}
                render={({ field, fieldState }) => {
                    letteringSequence.raw_Material_Number = field.value;

                    return (
                        <>
                            <InputText ref={field.ref} value={fieldState.isDirty ? field.value : options.value} maxLength={50} tooltip={fieldState.error?.message} tooltipOptions={{ position: 'bottom' }} className={classNames('w-12rem', { 'p-invalid border-left-10': fieldState.error })} onChange={(e) => { letteringSequence.raw_Material_Number = e.target.value; field.onChange(e.target.value); }} placeholder="Raw Material Number" />
                        </>
                    )} 
                }
            />              
        );
    }

    const word_Number_Editor = (options: any) => {
        return (
            <Controller 
                name="word_Number_Edit" 
                control={formEditLetteringSequence.control}
                defaultValue = {options.value}
                render={({ field, fieldState }) => {
                    letteringSequence.word_Number = field.value;

                    return (
                        <>
                            <InputNumber id={field.name} name={field.name} inputRef={field.ref} value={fieldState.isDirty ? field.value : options.value} tooltip={fieldState.error?.message} tooltipOptions={{ position: 'bottom' }} onBlur={field.onBlur} onValueChange={(e) => field.onChange(e)} inputClassName={classNames('w-5rem', { 'p-invalid border-left-10': fieldState.error })} min={0} max={2147483647} useGrouping={false} placeholder="0" />
                        </>
                    )} 
                }
            />              
        );
    }

    const sequence_Template = (rowData : LetteringSequence) => {
        return rowData.sequence;
    }

    const raw_Material_Number_Template = (rowData: LetteringSequence) => {
        return rowData.raw_Material_Number;
    }

    const quantity_Per_Letter_Template = (rowData: LetteringSequence) => {
        if (rowData.quantity_Per_Letter) {
            return (
                <>
                    <i className="pi pi-check text-green-500"></i>
                </>
            );
        } else {
            return (
                <>
                    <i className="pi pi-times text-red-500"></i>
                </>
            );
        }
    }

    const estimated_Count_Color1_Template = (rowData: LetteringSequence) => {
        return rowData.estimated_Count_Color1;
    }

    const estimated_Count_Color2_Template = (rowData: LetteringSequence) => {
        return rowData.estimated_Count_Color2;
    }

    const estimated_Count_Color3_Template = (rowData: LetteringSequence) => {
        return rowData.estimated_Count_Color3;
    }

    const letter_Size_Template = (rowData : LetteringSequence) => {
        return rowData.letter_Size;
    }

    const word_Number_Template = (rowData : LetteringSequence) => {
        return rowData.word_Number;
    }

    const rowEditorLetteringSequenceTemplate = (rowData: LetteringSequence, props: any) => {
        if (!styleCode) { return; }

        const rowEditor = props.rowEditor;

        if (rowEditor.editing) {
            isNew.current = false;
            
            return (
                <>
                    <Button icon="p-row-editor-save-icon pi pi-fw pi-save" title="Save" rounded text severity="info" type="submit" onClick={(e) => {
                        if (!formEditLetteringSequence.getFieldState("sequence_Edit").invalid && !formEditLetteringSequence.getFieldState("raw_Material_Number_Edit").invalid && !formEditLetteringSequence.getFieldState("quantity_Per_Letter_Edit").invalid && !formEditLetteringSequence.getFieldState("estimated_Count_Color1_Edit").invalid && !formEditLetteringSequence.getFieldState("estimated_Count_Color2_Edit").invalid && !formEditLetteringSequence.getFieldState("estimated_Count_Color3_Edit").invalid && !formEditLetteringSequence.getFieldState("letter_Size_Edit").invalid && !formEditLetteringSequence.getFieldState("word_Number_Edit").invalid) {
                            let _letteringSequence = { ...letteringSequence };
                            _letteringSequence.id = rowData.id;
                            _letteringSequence.entry_User = rowData.entry_User;
                            _letteringSequence.entry_Datetime = rowData.entry_Datetime;
                            _letteringSequence.transfer_Style = rowData.transfer_Style;
                            _letteringSequence.change_User = account !== null ? account.username : '';
                            _letteringSequence.change_Datetime = new Date().toISOString().slice(0, 19).replace('T', ' ');
                            let countDuplicate = 0;

                            letteringSequences.forEach(letteringSequence => {
                                if (letteringSequence.transfer_Style === styleCode 
                                    && letteringSequence.sequence === _letteringSequence.sequence 
                                    && letteringSequence.letter_Size === _letteringSequence.letter_Size 
                                    && letteringSequence.word_Number === _letteringSequence.word_Number) {
                                        countDuplicate +=1;
                                        return;
                                }
                            });

                            if (countDuplicate > 1) {
                                toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Duplicate entry detected based on sequence, style, letter size and word number.', life: 3000});
                                return;
                            }
                            
                            if (_letteringSequence.letter_Size && _letteringSequence.letter_Size > 99.99){
                                _letteringSequence.letter_Size = 99.99;
                            }

                            if (
                                _letteringSequence.estimated_Count_Color1 === null && 
                                _letteringSequence.estimated_Count_Color2 === null && 
                                _letteringSequence.estimated_Count_Color3 === null
                            ) {
                                toast.current.show({ severity: 'error', summary: 'Failed', detail: 'At least one quantity color should be filled.', life: 3000});
                                return;
                            }

                            saveLetteringSequence(_letteringSequence);
                            formEditLetteringSequence.clearErrors();
                            formEditLetteringSequence.reset();
                            rowEditor.onSaveClick(e);
                        }
                    }} />
                    
                    <Button icon="p-row-editor-cancel-icon pi pi-fw pi-undo" title="Undo" rounded text severity="danger" onClick={(e) => {
                        formEditLetteringSequence.clearErrors();
                        formEditLetteringSequence.reset();
                        rowEditor.onCancelClick(e);
                    }} />
                </>
            );
        } else {
            return (
                <>
                    <Link className="pi pi-fw pi-trash rowDelete" title="Delete" onClick={() => confirmDeleteLetteringSequence(rowData)} to={''}></Link>
                </>
            );
        }
    }

    const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let _filters = { ...filters };

        // @ts-ignore
        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilter(value);
    };

    const headerLetteringSequencesTable = (
        <div className="flex flex-column md:flex-row md:justify-content-end md:align-items-center">
            <IconField iconPosition="left">
                <InputIcon className="pi pi-search" />
                <InputText className="p-inputtext-sm" type="search" autoComplete="off" value={globalFilter} onChange={onGlobalFilterChange} placeholder="Search..." />
            </IconField>
        </div>
    );

    const deleteLetteringSequenceDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteLetteringSequenceDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteLetteringSequence} />
        </>
    );

    return (
        <div className="grid-nogutter">
            <div className="col-12">
                <Toast ref={toast} />

                <Panel header="Add Lettering Sequence" className="addPanel mb-2 -ml-2">
                    <form key={2} onSubmit={handleSubmitLetteringSequence(onSubmitLetteringSequence)} className="p-fluid" autoComplete="off">
                        <div className="formgrid grid">                          
                            <div className="field col-1">
                                <Controller 
                                    name="sequence" 
                                    control={controlLetteringSequence}
                                    defaultValue={null}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <label htmlFor={field.name} className="font-bold text-xs">Sequence</label>
                                            <InputNumber id={field.name} name={field.name} inputRef={field.ref} value={field.value} onBlur={field.onBlur} onValueChange={(e) => field.onChange(e)} inputClassName={classNames({ 'p-invalid border-left-10': fieldState.error })} min={0} max={2147483647} useGrouping={false}/>
                                            {getLetteringSequenceFormErrorMessage('sequence')}
                                        </>
                                    )} 
                                />                                    
                            </div>
                            <div className="field col-2">
                                <Controller 
                                    name="raw_Material_Number" 
                                    control={controlLetteringSequence}
                                    defaultValue={''} 
                                    rules = {{
                                        required: 'Raw Material Number is required'
                                    }} 
                                    render={({ field, fieldState }) => (
                                        <>
                                            <label htmlFor={field.name} className="font-bold text-xs">Raw Material Number</label>
                                            <InputText id={field.name} name={field.name} value={field.value} maxLength={50} className={classNames({ 'p-invalid border-left-10': fieldState.error })} onChange={(e) => field.onChange(e.target.value)} placeholder="Raw Material Number" />
                                            {getLetteringSequenceFormErrorMessage('raw_Material_Number')}
                                        </>
                                    )} 
                                />                                    
                            </div>
                            <div className="field col-fixed isCheckbox ml-3">
                                <Controller
                                    name="quantity_Per_Letter"
                                    control={controlLetteringSequence}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <label htmlFor={field.name} className="font-bold text-xs">Qty Per Letter</label><br />
                                            <Checkbox inputId={field.name} checked={field.value} inputRef={field.ref} className={classNames('col-offset-4', { 'p-invalid mr-1': fieldState.error })} onChange={(e) => field.onChange(e.checked)} />
                                        </>
                                    )}
                                />
                            </div>
                            <div className="field col-1">
                                <Controller 
                                    name="estimated_Count_Color1" 
                                    control={controlLetteringSequence}
                                    defaultValue= {0} 
                                    render={({ field, fieldState }) => (
                                        <>
                                            <label htmlFor={field.name} className="font-bold text-xs">Qty Color 1</label>
                                            <InputNumber id={field.name} name={field.name} inputRef={field.ref} value={field.value} onBlur={field.onBlur} onValueChange={(e) => field.onChange(e)} inputClassName={classNames({ 'p-invalid border-left-10': fieldState.error })} min={0} max={2147483647} useGrouping={false} placeholder="0" />
                                        </>
                                    )} 
                                />                                    
                            </div>
                            <div className="field col-1">
                                <Controller 
                                    name="estimated_Count_Color2" 
                                    control={controlLetteringSequence}
                                    defaultValue= {0} 
                                    render={({ field, fieldState }) => (
                                        <>
                                            <label htmlFor={field.name} className="font-bold text-xs">Qty Color 2</label>
                                            <InputNumber id={field.name} name={field.name} inputRef={field.ref} value={field.value} onBlur={field.onBlur} onValueChange={(e) => field.onChange(e)} inputClassName={classNames({ 'p-invalid border-left-10': fieldState.error })} min={0} max={2147483647} useGrouping={false} placeholder="0" />
                                        </>
                                    )} 
                                />
                            </div>
                            <div className="field col-1">
                                <Controller 
                                    name="estimated_Count_Color3" 
                                    control={controlLetteringSequence}
                                    defaultValue= {0} 
                                    render={({ field, fieldState }) => (
                                        <>
                                            <label htmlFor={field.name} className="font-bold text-xs">Qty Color 3</label>
                                            <InputNumber id={field.name} name={field.name} inputRef={field.ref} value={field.value} onBlur={field.onBlur} onValueChange={(e) => field.onChange(e)} inputClassName={classNames({ 'p-invalid border-left-10': fieldState.error })} min={0} max={2147483647} useGrouping={false} placeholder="0" />
                                        </>
                                    )} 
                                />                                    
                            </div> 
                            <div className="field col-1">
                                <Controller 
                                    name="letter_Size" 
                                    control={controlLetteringSequence}
                                    defaultValue= {null}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <label htmlFor={field.name} className="font-bold text-xs">Letter Size</label>
                                            <InputNumber id={field.name} name={field.name} inputRef={field.ref} value={field.value} onBlur={field.onBlur}
                                            inputClassName={classNames({ 'p-invalid border-left-10': fieldState.error })} 
                                            onChange={(e) => {
                                                    field.onChange(e.value);
                                            }}
                                            max={99.99}
                                            maxFractionDigits={2}
                                            format={true}
                                            mode='decimal' minFractionDigits={0} 
                                            placeholder="00.00" />
                                            {getLetteringSequenceFormErrorMessage('letter_Size')}
                                        </>
                                    )} 
                                />                                    
                            </div>
                            <div className="field col-1">
                                <Controller 
                                    name="word_Number" 
                                    control={controlLetteringSequence}
                                    defaultValue= {null} 
                                    render={({ field, fieldState }) => (
                                        <>
                                            <label htmlFor={field.name} className="font-bold text-xs w-6rem">Word Number</label>
                                            <InputNumber id={field.name} name={field.name} inputRef={field.ref} value={field.value} onBlur={field.onBlur} onValueChange={(e) => field.onChange(e)} inputClassName={classNames('w-6rem', { 'p-invalid border-left-10': fieldState.error })} min={0} max={2147483647} useGrouping={false} placeholder="0" />
                                            {getLetteringSequenceFormErrorMessage('word_Number')}
                                        </>
                                    )} 
                                />                                    
                            </div>      
                            <div className="field field-buttons col-2 pt-4 ml-3">
                                <Button icon="pi pi-save" title="Save" rounded text severity="info" type="submit" />
                                <Button icon="pi pi-undo" title="Undo" rounded text severity="danger" type="button" onClick={resetLetteringSequence}/>
                            </div>
                        </div>
                    </form>
                </Panel>
        
                <DataTable value={letteringSequences} className="-ml-2" selectionMode="single" selection={selectedLetteringSequence} onSelectionChange={setLetteringSequenceActiveRowIndex}
                    dataKey="id" size="small" rowHover={true} loading={loadingLetteringSequences}
                    scrollable scrollHeight="calc(100vh - 40rem)"
                    editingRows={editingRows} editMode="row" onRowEditChange={onRowEditChange}
                    filters={filters} emptyMessage="No lettering sequences found." header={headerLetteringSequencesTable}>
                    <Column field="sequence" header="Sequence" body={sequence_Template} editor={(options:ColumnEditorOptions) => cellEditor(options)} align="center" alignHeader="center"></Column>
                    <Column field="raw_Material_Number" header="Raw Material Number" body={raw_Material_Number_Template} editor={(options:ColumnEditorOptions) => cellEditor(options)} align="center" alignHeader="center"></Column>
                    <Column field="quantity_Per_Letter" header="Qty Per Letter" body={quantity_Per_Letter_Template} editor={(options:ColumnEditorOptions) => cellEditor(options)} align="center" alignHeader="center"></Column>
                    <Column field="estimated_Count_Color1" header="Qty Color 1" body={estimated_Count_Color1_Template} editor={(options:ColumnEditorOptions) => cellEditor(options)} align="center" alignHeader="center"></Column>
                    <Column field="estimated_Count_Color2" header="Qty Color 2" body={estimated_Count_Color2_Template} editor={(options:ColumnEditorOptions) => cellEditor(options)} align="center" alignHeader="center"></Column>
                    <Column field="estimated_Count_Color3" header="Qty Color 3" body={estimated_Count_Color3_Template} editor={(options:ColumnEditorOptions) => cellEditor(options)} align="center" alignHeader="center"></Column>
                    <Column field="letter_Size" header="Letter Size" body={letter_Size_Template} editor={(options:ColumnEditorOptions) => cellEditor(options)} align="center" alignHeader="center"></Column>
                    <Column field="word_Number" header="Word Number" body={word_Number_Template} editor={(options:ColumnEditorOptions) => cellEditor(options)} align="center" alignHeader="center"></Column>
                    <Column rowEditor body={rowEditorLetteringSequenceTemplate} bodyStyle={{ textAlign: 'center' }}></Column>
                </DataTable>
        
                <Dialog visible={deleteLetteringSequenceDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteLetteringSequenceDialogFooter} onHide={hideDeleteLetteringSequenceDialog}>
                    <div className="flex align-items-center justify-content-center">
                        <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                        {letteringSequence && <span>Are you sure you want to delete the lettering sequence for the <b>{letteringSequence.raw_Material_Number}</b> raw material number ?</span>}
                    </div>
                </Dialog>
            </div>
        </div>
    );
};

export default StyleLettering;